import React from 'react';
import { useForm } from 'react-hook-form';
import { Goal, Matter, Project, Client } from '../../lib/db';

// Props interface for the GoalForm component
// goal: Existing goal to edit (undefined for creating new goal)
// matters: List of available matters to choose from
// projects: List of available projects to choose from  
// clients: List of available clients to choose from
// onSubmit: Function called when form is submitted with form data
// onCancel: Function called when user cancels the form
interface GoalFormProps {
  goal?: Goal;
  matters?: Matter[];
  projects?: Project[];
  clients?: Client[];
  onSubmit: (data: Partial<Goal>) => Promise<void>;
  onCancel: () => void;
}

/**
 * GoalForm Component
 * 
 * This component provides a comprehensive form for creating or editing deliverables (goals).
 * In the app, "goals" are actually called "deliverables" in the UI - they represent
 * specific work items that need to be completed for clients, projects, or matters.
 * 
 * The form includes:
 * 1. Basic information: title, description, category
 * 2. Organization: ability to link to projects, matters, or clients
 * 3. Scheduling: start date and deadline
 * 4. Priority: numeric priority from 0-10
 * 5. Form validation to ensure required fields are filled
 * 6. Data cleaning to handle empty optional fields properly
 * 
 * Note: This form handles both creating new deliverables and editing existing ones.
 * When editing, the form is pre-populated with existing values.
 */
function GoalForm({ goal, matters, projects, clients, onSubmit, onCancel }: GoalFormProps) {
  // Set up form handling with react-hook-form
  // This manages form state, validation, and submission
  const { register, handleSubmit, formState: { errors } } = useForm({
    // Pre-populate form with existing goal data when editing, or empty values when creating
    defaultValues: goal || {
      title: '',
      description: '',
      category: '',
      priority: 0,
      matter_id: '',
      project_id: '',
      client_id: '',
      // Convert dates to string format required by HTML date inputs
      start_date: goal?.start_date ? new Date(goal.start_date).toISOString().split('T')[0] : '',
      deadline: goal?.deadline ? new Date(goal.deadline).toISOString().split('T')[0] : '',
    },
  });

  // Function to handle form submission and data cleanup
  const handleFormSubmit = (data: Partial<Goal>) => {
    // Convert empty strings to undefined for optional fields
    // This prevents sending empty strings to the database, which can cause issues
    const cleanedData = {
      ...data,
      matter_id: data.matter_id || undefined,      // Remove empty matter selection
      project_id: data.project_id || undefined,    // Remove empty project selection
      client_id: data.client_id || undefined,      // Remove empty client selection
      description: data.description || undefined,   // Remove empty description
      category: data.category || undefined,         // Remove empty category
      start_date: data.start_date || undefined,     // Remove empty start date
      deadline: data.deadline || undefined,         // Remove empty deadline
    };
    // Pass the cleaned data to the parent component's submit handler
    onSubmit(cleanedData);
  };

  return (
    // Main form container with spacing between sections
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {/* Title field (required) */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
          Title
        </label>
        <input
          type="text"
          id="title"
          {...register('title', { required: 'Title is required' })}  // Form validation rule
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
        {/* Show error message if title validation fails */}
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
        )}
      </div>

      {/* Description field (optional) */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          id="description"
          rows={3}
          {...register('description')}  // No validation - optional field
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
      </div>

      {/* Project selection dropdown (only show if projects are available) */}
      {projects && projects.length > 0 && (
        <div>
          <label htmlFor="project_id" className="block text-sm font-medium text-gray-700">
            Project (Optional)
          </label>
          <select
            id="project_id"
            {...register('project_id')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">No project selected</option>
            {/* Render each available project as an option */}
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.title}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Matter selection dropdown (only show if matters are available) */}
      {matters && matters.length > 0 && (
        <div>
          <label htmlFor="matter_id" className="block text-sm font-medium text-gray-700">
            Matter (Optional)
          </label>
          <select
            id="matter_id"
            {...register('matter_id')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">No matter selected</option>
            {/* Render each available matter as an option */}
            {matters.map((matter) => (
              <option key={matter.id} value={matter.id}>
                {matter.title}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Client selection dropdown (only show if clients are available) */}
      {clients && clients.length > 0 && (
        <div>
          <label htmlFor="client_id" className="block text-sm font-medium text-gray-700">
            Client (Optional)
          </label>
          <select
            id="client_id"
            {...register('client_id')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">No client</option>
            {/* Render each available client as an option */}
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Two-column layout for category and priority */}
      <div className="grid grid-cols-2 gap-4">
        {/* Category field (optional) */}
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700">
            Category
          </label>
          <input
            type="text"
            id="category"
            {...register('category')}  // No validation - optional field
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>

        {/* Priority field (required, 0-10 range) */}
        <div>
          <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
            Priority (0-10)
          </label>
          <input
            type="number"
            id="priority"
            min="0"
            max="10"
            {...register('priority', {
              required: 'Priority is required',
              min: { value: 0, message: 'Priority must be at least 0' },
              max: { value: 10, message: 'Priority must be at most 10' },
            })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
          {/* Show error message if priority validation fails */}
          {errors.priority && (
            <p className="mt-1 text-sm text-red-600">{errors.priority.message}</p>
          )}
        </div>
      </div>

      {/* Two-column layout for dates */}
      <div className="grid grid-cols-2 gap-4">
        {/* Start date field (optional) */}
        <div>
          <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
            Start Date (Optional)
          </label>
          <input
            type="date"
            id="start_date"
            {...register('start_date')}  // No validation - optional field
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>

        {/* Deadline field (optional) */}
        <div>
          <label htmlFor="deadline" className="block text-sm font-medium text-gray-700">
            Deadline (Optional)
          </label>
          <input
            type="date"
            id="deadline"
            {...register('deadline')}  // No validation - optional field
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Form action buttons */}
      <div className="flex justify-end space-x-3">
        {/* Cancel button - calls onCancel without submitting form */}
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        {/* Submit button - text changes based on whether editing or creating */}
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          {goal ? 'Update Deliverable' : 'Create Deliverable'}
        </button>
      </div>
    </form>
  );
}

export default GoalForm;